import { supabase } from "../lib/supabase";
import { logExtractedEventMutation } from "../lib/productEvents";
import {
  beginPendingMutation,
  claimPendingMutation,
  finishPendingMutation,
  stampPendingUser,
} from "../lib/pendingMutations";

const API_BASE = process.env.REACT_APP_API_BASE || "";

async function authHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error("Bitte melde dich an.");
  }
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

/**
 * The signed-in user's forwarded-email extractions (newest first).
 * @returns {Promise<{ items: Array<object> }>}
 */
export async function fetchSchoolEmails() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/school-emails`, { headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Laden fehlgeschlagen (${res.status}).`);
  }
  return data;
}

/**
 * Record manual accuracy corrections for one extraction.
 * @param {{ id: string, reviewStatus?: string, corrections?: object, missedCount?: number }} params
 */
export async function submitSchoolEmailReview({
  id,
  reviewStatus,
  corrections,
  missedCount,
}) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/school-emails/review`, {
    method: "POST",
    headers,
    body: JSON.stringify({ id, reviewStatus, corrections, missedCount }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Speichern fehlgeschlagen (${res.status}).`);
  }
  return data;
}

/**
 * Inline date/time correction or dismissal for one extracted event.
 * @param {string} id extraction row id
 * @param {number} eventIndex
 * @param {{ date?: string, time?: string|null, all_day?: boolean, title?: string, dismiss?: boolean, remind?: boolean, done?: boolean, child?: string|null }} body
 */
export async function patchSchoolEmailEventOverride(id, eventIndex, body, opts = {}) {
  const pendingId = opts.fromQueue
    ? opts.pendingId
    : beginPendingMutation({
        kind: "school-event",
        targetId: id,
        eventIndex,
        body,
      });
  if (opts.fromQueue) claimPendingMutation(pendingId);
  const hold = { kind: "school-event", targetId: id, eventIndex, body };
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.user?.id) stampPendingUser(pendingId, session.user.id);
    const headers = await authHeaders();
    const res = await fetch(
      `${API_BASE}/api/school-emails/${encodeURIComponent(id)}/events/${encodeURIComponent(String(eventIndex))}`,
      {
        method: "PATCH",
        headers,
        body: JSON.stringify(body),
        keepalive: true,
      },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = new Error(data.error || `Speichern fehlgeschlagen (${res.status}).`);
      error.status = res.status;
      finishPendingMutation(pendingId, { ok: false, status: res.status });
      throw error;
    }
    finishPendingMutation(pendingId, { ok: true, hold });
    // scheduleItem comes from the override API after re-project (mail origin).
    logExtractedEventMutation({ item: data?.scheduleItem, body });
    return data;
  } catch (err) {
    if (!err?.status) finishPendingMutation(pendingId, { ok: false });
    throw err;
  }
}

/**
 * Admin: aggregated extraction feedback stats.
 * @param {{ days?: number }} [params]
 */
export async function fetchSchoolExtractionStats({ days = 30 } = {}) {
  const headers = await authHeaders();
  const res = await fetch(
    `${API_BASE}/api/admin/school-extraction-stats?days=${encodeURIComponent(String(days))}`,
    { headers }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Statistik fehlgeschlagen (${res.status}).`);
  }
  return data;
}

/**
 * The signed-in user's actionable KiTa deadlines in the next N days.
 * @param {{ days?: number }} [params]
 */
export async function fetchSchoolEmailsUpcoming({ days = 7 } = {}) {
  const headers = await authHeaders();
  const res = await fetch(
    `${API_BASE}/api/school-emails/upcoming?days=${encodeURIComponent(String(days))}`,
    { headers },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Laden fehlgeschlagen (${res.status}).`);
  }
  return data;
}

/**
 * Open Aufgaben from KiTa mail (includes overdue).
 * @param {{ days?: number }} [params]
 */
export async function fetchSchoolTasks({ days = 90 } = {}) {
  const headers = await authHeaders();
  const res = await fetch(
    `${API_BASE}/api/school-emails/tasks?days=${encodeURIComponent(String(days))}`,
    { headers },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Laden fehlgeschlagen (${res.status}).`);
  }
  return data;
}

/**
 * Batch update reminder preferences for extracted events.
 * @param {{ updates: Array<{ id: string, eventIndex: number, remind: boolean }> }} params
 */
export async function submitSchoolRemindPrefs({ updates }) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/school-emails/remind-prefs`, {
    method: "POST",
    headers,
    body: JSON.stringify({ updates }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Speichern fehlgeschlagen (${res.status}).`);
  }
  return data;
}

/**
 * Rebuild KiTa deadline nudges after KiTa-Tage are saved.
 */
export async function rescheduleSchoolDeadlineNudges() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/school-emails/reschedule-deadlines`, {
    method: "POST",
    headers,
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Speichern fehlgeschlagen (${res.status}).`);
  }
  return data;
}

/**
 * Get-or-create this user's KiTa email forwarding address.
 * @returns {Promise<{ token: string, address: string|null, domain: string|null, localPart: string|null }>}
 */
export async function fetchSchoolEmailAlias() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/school-emails/alias/mine`, { headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Adresse konnte nicht geladen werden (${res.status}).`);
  }
  return data;
}

/**
 * Upload a PDF for an extraction whose attachment was dropped or unreadable.
 * Re-runs deadline extraction on body + PDF text.
 * @param {string} id extraction row id
 * @param {File} file PDF file
 */
export async function uploadSchoolEmailPdf(id, file) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error("Bitte melde dich an.");
  }

  const form = new FormData();
  form.append("pdf", file);

  const res = await fetch(`${API_BASE}/api/school-emails/${encodeURIComponent(id)}/upload-pdf`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `PDF-Upload fehlgeschlagen (${res.status}).`);
  }
  return data;
}
